import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkle, PencilSimple, FloppyDisk, ArrowClockwise, Trash,
  X, Baby, User, Wheelchair, Heartbeat, House, Package,
} from "@phosphor-icons/react";

/**
 * Iter 148 · SmartDataTab
 * Superadmin/admin/manager can see this. Only superadmin can edit.
 * Rigo silently extracts customer profile fields from chat and stores
 * them here so useful data survives chat deletion.
 */

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
};

const SECTIONS = [
  {
    key: "household", label: "Household", icon: House,
    fields: [
      { key: "full_name", label: "Full name" },
      { key: "mobile_number", label: "Mobile number" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address", long: true },
      { key: "location_gps", label: "Location (GPS)" },
      { key: "residence_type", label: "Residential / Commercial",
         options: ["residential", "commercial"] },
    ],
  },
  {
    key: "service", label: "Service", icon: Package,
    fields: [
      { key: "waste_types", label: "Waste type(s)", multi: true },
      { key: "collection_frequency", label: "Collection frequency",
         options: ["weekly", "alternate", "monthly", "on_demand"] },
      { key: "approximate_quantity", label: "Approximate quantity" },
      { key: "preferred_pickup_day", label: "Preferred pickup day" },
      { key: "preferred_pickup_time", label: "Preferred pickup time" },
      { key: "vehicle_accessibility", label: "Vehicle accessibility",
         options: ["2 Wheeler", "3 Wheeler", "4 Wheeler", "6 Wheeler"] },
    ],
  },
  {
    key: "baby_care", label: "Baby Care", icon: Baby,
    fields: [
      { key: "first_name", label: "Baby's first name" },
      { key: "age_group", label: "Age group",
         options: ["0-6 months", "6-12 months", "1-2 years",
                    "2-3 years", "Above 3 years"] },
      { key: "weekly_diaper_usage", label: "Weekly diaper usage",
         options: ["5-10", "10-20", "20-40", "40+"] },
    ],
  },
  {
    key: "adult_care", label: "Adult Care", icon: Wheelchair,
    fields: [
      { key: "relationship_to_patient", label: "Relationship to patient",
         options: ["yourself", "parent", "grandparent",
                    "family_member", "someone_else"] },
      { key: "weekly_diaper_usage", label: "Weekly diaper usage",
         options: ["5-10", "10-20", "20-40", "40+"] },
    ],
  },
  {
    key: "healthcare_interest", label: "Healthcare Interest", icon: Heartbeat,
    booleans: [
      { key: "home_nursing", label: "Home nursing" },
      { key: "physiotherapy", label: "Physiotherapy" },
      { key: "lab_tests", label: "Lab tests" },
      { key: "doctor_visits", label: "Doctor visits" },
      { key: "palliative_care", label: "Palliative care" },
      { key: "medical_equipment", label: "Medical equipment" },
    ],
    fields: [{ key: "other_services", label: "Other services of interest",
                long: true }],
  },
];

export default function SmartDataTab({ customerId, canEdit }) {
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [editing, setEdit]  = useState(false);
  const [draft, setDraft]   = useState(null);
  const [showHist, setHist] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/crm/customers/${customerId}/smart-profile`);
      setData(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data?.profile || {})));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [customerId]);

  const rebuild = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/crm/customers/${customerId}/smart-profile/rebuild`, {});
      toast.success("Profile refreshed from latest chat");
      if (r.data?.profile) {
        setData((d) => ({ ...d, profile: r.data.profile,
                            updated_at: new Date().toISOString() }));
        setDraft(JSON.parse(JSON.stringify(r.data.profile)));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/crm/customers/${customerId}/smart-profile`,
        { profile: draft });
      toast.success("Saved");
      setEdit(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const clear = async () => {
    if (!confirm("Clear the entire Smart Data profile for this customer?")) return;
    setBusy(true);
    try {
      await api.delete(`/crm/customers/${customerId}/smart-profile`);
      toast.success("Cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const setField = (section, field, value) => {
    setDraft((d) => ({
      ...d,
      [section]: { ...(d?.[section] || {}), [field]: value },
    }));
  };
  const view = editing ? draft : data?.profile;

  const custPref = editing ? draft?.customer_preferences : data?.profile?.customer_preferences;
  const setCustPref = (v) => setDraft((d) => ({ ...d, customer_preferences: v }));

  // Density-1 badge for completeness
  const completeness = useMemo(() => {
    if (!data?.profile) return 0;
    const p = data.profile;
    let filled = 0, total = 0;
    for (const sec of SECTIONS) {
      for (const f of (sec.fields || [])) {
        total++;
        const v = p[sec.key]?.[f.key];
        if (v && !(Array.isArray(v) && v.length === 0)) filled++;
      }
      for (const b of (sec.booleans || [])) {
        total++;
        if (p[sec.key]?.[b.key]) filled++;
      }
    }
    total++; if (p.customer_preferences) filled++;
    return Math.round((filled / total) * 100);
  }, [data]);

  if (!data) return <div className="text-sm text-[#8A8780]">Loading Smart Data…</div>;

  return (
    <div className="space-y-4" data-testid="smart-data-tab">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0F1F0C] to-[#1B3B18] text-white rounded-md p-4 flex flex-wrap items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0">
          <Sparkle size={20} weight="fill" className="text-[#D4E0D1]"/>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-lg leading-tight">
            Smart Customer Profile (AI extracted)
          </div>
          <div className="text-[12px] text-white/70 mt-0.5">
            Silently built from Rigo chat conversations · updated {fmtDate(data.updated_at)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/60">Completeness</div>
          <div className="text-2xl font-semibold">{completeness}<span className="text-sm opacity-70">%</span></div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2" data-testid="smart-actions">
        {canEdit && !editing && (
          <button onClick={() => setEdit(true)} data-testid="smart-edit"
                  className="h-9 px-3 rounded-md border border-[#E5E2D9] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <PencilSimple size={12}/>Edit
          </button>
        )}
        {canEdit && editing && (
          <>
            <button onClick={save} disabled={busy} data-testid="smart-save"
                    className="h-9 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-medium inline-flex items-center gap-1.5">
              <FloppyDisk size={12} weight="bold"/>{busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEdit(false); setDraft(JSON.parse(JSON.stringify(data.profile || {}))); }}
                    data-testid="smart-cancel"
                    className="h-9 px-3 rounded-md border border-[#E5E2D9] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
              <X size={12}/>Cancel
            </button>
          </>
        )}
        {canEdit && (
          <button onClick={rebuild} disabled={busy} data-testid="smart-rebuild"
                  className="h-9 px-3 rounded-md border border-[#E5E2D9] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <ArrowClockwise size={12}/>Rebuild from chat
          </button>
        )}
        {canEdit && (
          <button onClick={clear} disabled={busy} data-testid="smart-clear"
                  className="h-9 px-3 rounded-md border border-[#E5E2D9] text-[#7A2E18] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7EFEC] ml-auto">
            <Trash size={12}/>Clear all
          </button>
        )}
      </div>

      {/* Sections */}
      <div className="grid gap-3 md:grid-cols-2">
        {SECTIONS.map((sec) => (
          <div key={sec.key} data-testid={`smart-section-${sec.key}`}
               className="bg-white border border-[#E5E2D9] rounded-md p-4">
            <div className="flex items-center gap-2 mb-3">
              <sec.icon size={16} weight="duotone" className="text-[#2D5A27]"/>
              <div className="font-heading font-medium text-sm">{sec.label}</div>
            </div>
            <div className="space-y-2">
              {(sec.fields || []).map((f) => (
                <Row key={f.key} label={f.label}>
                  <Value
                    value={view?.[sec.key]?.[f.key]}
                    editing={editing}
                    onChange={(v) => setField(sec.key, f.key, v)}
                    options={f.options}
                    multi={f.multi}
                    long={f.long}
                    testid={`smart-${sec.key}-${f.key}`}
                  />
                </Row>
              ))}
              {(sec.booleans || []).length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  {sec.booleans.map((b) => (
                    <label key={b.key}
                            className="inline-flex items-center gap-1.5 text-xs">
                      <input type="checkbox"
                              disabled={!editing}
                              data-testid={`smart-${sec.key}-${b.key}`}
                              checked={!!view?.[sec.key]?.[b.key]}
                              onChange={(e) => setField(sec.key, b.key, e.target.checked)}/>
                      <span>{b.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Customer preferences (top-level) */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4 md:col-span-2"
             data-testid="smart-section-preferences">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} weight="duotone" className="text-[#2D5A27]"/>
            <div className="font-heading font-medium text-sm">Customer preferences</div>
          </div>
          {editing ? (
            <textarea value={custPref || ""}
                       onChange={(e) => setCustPref(e.target.value)}
                       rows={3} data-testid="smart-preferences-input"
                       className="w-full p-2 border border-[#E5E2D9] rounded text-sm focus:outline-none focus:border-[#2D5A27]"
                       placeholder="Any preferences customer has shared…"/>
          ) : (
            <div className="text-sm text-[#0F1F0C] whitespace-pre-wrap min-h-[24px]"
                 data-testid="smart-preferences-view">
              {custPref || <span className="text-[#8A8780]">— none captured yet —</span>}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
        <button onClick={() => setHist((v) => !v)}
                 data-testid="smart-history-toggle"
                 className="text-xs text-[#2D5A27] font-medium">
          {showHist ? "Hide" : "Show"} extraction history ({data.history?.length || 0})
        </button>
        {showHist && (
          <div className="mt-3 space-y-1.5" data-testid="smart-history">
            {(data.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="text-[11px] text-[#5C5A55] flex flex-wrap gap-x-2">
                <span>{fmtDate(h.at)}</span>
                <span>·</span>
                <span className="font-medium text-[#0F1F0C]">
                  {h.source === "manual_edit" ? "Manual edit" : "AI extract"}
                </span>
                {h.editor_name && <span>· by {h.editor_name}</span>}
                {h.conversation_id && (
                  <span className="text-[#8A8780]">· conv {h.conversation_id.slice(0, 8)}</span>
                )}
              </div>
            ))}
            {(!data.history || data.history.length === 0) && (
              <div className="text-xs text-[#8A8780]">No history yet.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Row + Value primitives ────────────────────────────────────────
function Row({ label, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">{label}</span>
      <span className="text-sm text-[#0F1F0C] mt-0.5">{children}</span>
    </div>
  );
}

function Value({ value, editing, onChange, options, multi, long, testid }) {
  if (!editing) {
    if (multi && Array.isArray(value)) {
      if (value.length === 0) return <span className="text-[#8A8780]">—</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="px-2 h-5 rounded-full inline-flex items-center text-[10px] bg-[#EEEBE2]">
              {v}
            </span>
          ))}
        </div>
      );
    }
    if (value === true) return <span>Yes</span>;
    if (value === false) return <span>No</span>;
    return value ? <span data-testid={testid}>{String(value)}</span>
                  : <span className="text-[#8A8780]">—</span>;
  }
  if (multi) {
    const arr = Array.isArray(value) ? value : [];
    return (
      <input value={arr.join(", ")}
              placeholder="Comma-separated"
              data-testid={testid}
              onChange={(e) => onChange(
                e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-xs focus:outline-none focus:border-[#2D5A27]"/>
    );
  }
  if (options) {
    return (
      <select value={value ?? ""} data-testid={testid}
               onChange={(e) => onChange(e.target.value || null)}
               className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-xs bg-white focus:outline-none focus:border-[#2D5A27]">
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (long) {
    return (
      <textarea value={value ?? ""} rows={2} data-testid={testid}
                 onChange={(e) => onChange(e.target.value || null)}
                 className="w-full p-2 border border-[#E5E2D9] rounded text-xs focus:outline-none focus:border-[#2D5A27]"/>
    );
  }
  return (
    <input value={value ?? ""} data-testid={testid}
            onChange={(e) => onChange(e.target.value || null)}
            className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-xs focus:outline-none focus:border-[#2D5A27]"/>
  );
}
